import { Download, Sync } from '@mui/icons-material';
import { Box, Button, Dialog, Stack, Typography } from '@mui/material';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { HeaderComponent } from '../../layout/components/HeaderComponent.tsx';
import { OperatorRoutedModalContent } from '../../layout/components/OperatorRoutedModalContent.tsx';
import { ReadOnlyTextField } from '../../ui/material-ui/ReadOnlyTextField.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import {
    DefaultServerHeadersContext,
    RequestStatus,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import {
    LATEST_GITHUB_RELEASE_API_URL,
    LATEST_GITHUB_RELEASE_OVERVIEW_URL,
    parseRelease,
} from 'dg-web-shared/model/GithubRelease.ts';

// GitHub must not receive the operator header the portal adds to its own API calls.
const NO_DEFAULT_HEADERS = {};

export function SyncAppModal({
    open,
    onClose,
}: {
    open: boolean;
    onClose: () => void;
}) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            slotProps={{
                paper: {
                    sx: {
                        width: '576px',
                        maxHeight: '650px',
                        borderRadius: 0,
                    },
                },
            }}
        >
            <OperatorRoutedModalContent
                header={
                    <HeaderComponent
                        title="Sync App"
                        Icon={Sync}
                        editMode={false}
                        buttons={
                            <Button variant="outlined" onClick={onClose}>
                                <Localized
                                    de="Schliessen"
                                    fr="Fermer"
                                    it="Chiudi"
                                    en="Close"
                                />
                            </Button>
                        }
                    />
                }
                body={
                    <DefaultServerHeadersContext.Provider
                        value={NO_DEFAULT_HEADERS}
                    >
                        <SyncAppModalBody />
                    </DefaultServerHeadersContext.Provider>
                }
            />
        </Dialog>
    );
}

function SyncAppModalBody() {
    const [releaseState] = useServerFetch<unknown, object>(
        () => ({ url: LATEST_GITHUB_RELEASE_API_URL }),
        {},
    );
    const release =
        releaseState.status === RequestStatus.SUCCESS
            ? parseRelease(releaseState.data)
            : null;

    return (
        <Stack sx={{ gap: '24px' }}>
            <Stack sx={{ gap: '12px' }}>
                <Typography>
                    <Localized
                        de="Um Daten vom Parkingportal auf eine Parkuhr und wieder zurück zu synchronisieren, wird die Parkingportal Sync Applikation benötigt. Diese können Sie mit untenstehendem Link herunterladen und installieren."
                        fr="Synchroniser des données du Parkingportal vers un horodateur et vice-versa nécessite l'application Parkingportal Sync que vous pouvez télécharger et installer via le lien ci-dessous."
                        it="Per sincronizzare i dati che vanno dalla piattaforma Parkingportal a un parchimetro e viceversa, si utilizza l'applicazione Parkingportal Sync, da scaricare e installare tramite il seguente link."
                        en="To synchronize data between the Parkingportal and any parking meter, the Parkingportal Sync Application is required. You can download and install it using the link below."
                    />
                </Typography>
                <Typography>
                    <Localized
                        de="Die Parkingportal Sync App öffnet automatisch wenn ein entsprechender Link im Portal geklickt wird. Wählen Sie danach ihren Leerungs- oder Unterhaltsschlüssel aus der Liste um die Aktion abzuschliessen."
                        fr="L'application Parkingportal Sync s'ouvre automatiquement en cas de clic sur un lien correspondant dans le portail. Sélectionnez ensuite votre clé de collecte ou de maintenance dans la liste pour terminer l'opération."
                        it="L'applicazione Parkingportal Sync si apre in automatico cliccando sul link corrispondente nel portale. Selezionare la chiave di vuotatura o manutenzione per terminare l'operazione."
                        en="The Parkingportal Sync Application opens automatically when a respective link is clicked in the Parkingportal. Afterwards you can choose your Collection- or Maintenance-Stick from a list to complete the action."
                    />
                </Typography>
            </Stack>
            <ReadOnlyTextField
                label={
                    <Localized
                        de="Funktionen"
                        fr="Fonctions"
                        it="Funzioni"
                        en="Functionality"
                    />
                }
                value={
                    <Box component="ul" sx={{ margin: 0, paddingLeft: '28px' }}>
                        <li>
                            <Localized
                                de="Import von Leerungsdaten"
                                fr="Importation de données de collecte"
                                it="Importazione dei dati di vuotatura"
                                en="Collection data import"
                            />
                        </li>
                        <li>
                            <Localized
                                de="Aktualisierung von USB Sticks"
                                fr="Actualisation de clés USB"
                                it="Aggiornamento chiavette USB"
                                en="USB stick updates"
                            />
                        </li>
                        <li>
                            <Localized
                                de="Aktualisierung von TOMeco Konfigurationen"
                                fr="Actualisation des configurations TOMeco"
                                it="Aggiornamento delle configurazioni TOMeco"
                                en="TOMeco configuration updates"
                            />
                        </li>
                        <li>
                            <Localized
                                de="USB Stick Identifizierung"
                                fr="Identification de la clé USB"
                                it="Identificazione chiavetta USB"
                                en="USB stick identification"
                            />
                        </li>
                        <li>
                            <Localized
                                de="Automatische Updates"
                                fr="Mises à jour automatiques"
                                it="Aggiornamento automatico"
                                en="Automatic updates"
                            />
                        </li>
                    </Box>
                }
            />
            <ReadOnlyTextField
                label={
                    <Localized
                        de="Neuste Version"
                        fr="Dernière version"
                        it="Ultima versione"
                        en="Newest version"
                    />
                }
                value={
                    release
                        ? `${release.name} (${Formatter.dayMonthYear(release.published_at)})`
                        : EmDash
                }
            />
            <ReadOnlyTextField
                label={
                    <Localized
                        de="Systemanforderungen"
                        fr="Configuration requise"
                        it="Requisiti di sistema"
                        en="System Requirements"
                    />
                }
                value={
                    <Localized
                        de="Windows 7 x86/x64 oder neuer"
                        fr="Windows 7 x86/x64 ou plus récent"
                        it="Windows 7 x86/x64 o versione successiva"
                        en="Windows 7 x86/x64 or newer"
                    />
                }
            />
            <Button
                variant="contained"
                startIcon={<Download />}
                href={LATEST_GITHUB_RELEASE_OVERVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ alignSelf: 'flex-start' }}
            >
                <Localized
                    de="Neuste Version herunterladen"
                    fr="Télécharger la dernière version"
                    it="Scaricare l'ultima versione"
                    en="Download newest version"
                />
            </Button>
        </Stack>
    );
}
